import { memo, useCallback, useEffect, useState } from 'react';

import { Edit2, More, Trash } from 'iconsax-react';
import { useTheme } from 'styled-components';

import { Tooltip } from '@/shared/components/Tooltip';
import { useTooltip } from '@/shared/hooks/useTooltip';
import { Text } from '@develop-fapp/ui-kit-fapp';

import { Address } from '../../models/address';
import { getFormattedAddress } from '../../utils';
import { Wrapper } from './styles';

interface CardAddressProps {
  userAddress: Address;
  isCurrentAddress: boolean;
  onSelect: (addressToSelect: Address) => void;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  className?: string;
}

const Card: React.FC<CardAddressProps> = ({
  userAddress,
  isCurrentAddress,
  onSelect,
  onEdit,
  onDelete,
  className,
}) => {
  const { colors } = useTheme();

  const { isShowTooltipAfterInactivity } = useTooltip();

  const [isSelected, setIsSelected] = useState(false);

  const [iconColor, setIconColor] = useState(colors.grey3);

  const {
    id,
    street,
    neighborhood,
    number,
    city,
    state,
    title,
    complement,
    referencePoint,
  } = userAddress;

  useEffect(() => {
    setIconColor(isSelected ? colors.green2 : colors.grey3);
  }, [isSelected, colors]);

  const getComplementAndReferencePoint = () => {
    if (complement && referencePoint) return `${complement}, ${referencePoint}`;
    if (complement) return complement;
    return referencePoint;
  };

  const handleOnExpand = useCallback(
    () => setIsSelected(selected => !selected),
    [],
  );

  const addressWithNumber = number === '0' ? 'S/N' : number;

  const handleOnSelected = () => {
    onSelect(userAddress);
  };

  return (
    <Wrapper
      isCurrentAddress={isCurrentAddress}
      isSelected={isSelected}
      className={className}
    >
      <div>
        <div onClick={handleOnSelected}>
          {title && (
            <Text variant="h8" color={colors.grey1} weight="bold">
              {title}
            </Text>
          )}
          <Text style={{ margin: '4px 0' }} variant="h9" weight="medium">
            {getFormattedAddress(
              street,
              neighborhood,
              addressWithNumber,
              city,
              state,
            )}
          </Text>
          <Text variant="h9" color={colors.grey3}>
            {getComplementAndReferencePoint()}
          </Text>
        </div>
        <div>
          <Tooltip
            active={
              isShowTooltipAfterInactivity('address') &&
              isCurrentAddress &&
              !isSelected
            }
            validateChange
            content="Altere ou exclua seu endereço."
            direction="bottom-left"
            horizontalAlign={0.265}
            verticalAlign={1.5}
          >
            <More
              className="btn-show-more-address"
              color={iconColor}
              variant="Outline"
              onClick={handleOnExpand}
            />
          </Tooltip>
        </div>
      </div>
      {isSelected && (
        <div>
          <Edit2
            size={24}
            color={iconColor}
            onClick={() => onEdit(id ?? '')}
            className="btn-edit-address"
          />
          {!isCurrentAddress && (
            <Trash
              size={24}
              color={iconColor}
              onClick={() => onDelete(id ?? '')}
            />
          )}
        </div>
      )}
    </Wrapper>
  );
};

export const CardAddress = memo(Card);
